import { useIntersection } from 'lib/use-intersection'
import React, { FC, useRef, useState } from 'react'
import styled from 'styled-components'

import { getProjectName } from '../../lib/get-project-name'
import { Loader } from '../loader'

export type SourceProps = {
  src: string
  mediaRule: string
  contentType?: string
}

export type Props = {
  src: string
  sources?: SourceProps[]
  alt: string
  lazy?: boolean
  fit: 'cover' | 'contain'
  ratio?: {
    x: number
    y: number
  }
  setNaturalWidth?: (value: number) => void
  setNaturalHeight?: (value: number) => void
}

export const Image: FC<Props> = ({
  src,
  sources,
  alt,
  lazy = true,
  fit,
  ratio = { x: 16, y: 9 },
  setNaturalWidth = () => {},
  setNaturalHeight = () => {}
}) => {
  const [loaded, setLoaded] = useState(false)

  const pictureRef = useRef<HTMLDivElement>(null)

  // Todo: не создавать обсервера, если картинка не lazy (вынести отдельно Image и LazyImage)?
  const intersected = useIntersection(pictureRef, { root: null, rootMargin: '0px', threshold: 0 }, true)

  const visible = lazy ? intersected : true

  return (
    <Wrapper ref={pictureRef} ratio={ratio}>
      {!loaded && (
        <Placeholder>
          <Loader />
        </Placeholder>
      )}
      {visible && (
        <picture>
          {sources?.map((source: SourceProps, index) => (
            <source key={index} media={source.mediaRule} type={source.contentType} srcSet={source.src} />
          ))}
          <Img
            alt={alt}
            src={src}
            fit={fit}
            isVisible={loaded}
            onLoad={(e) => {
              setLoaded(true)
              setNaturalWidth(e.currentTarget.naturalWidth)
              setNaturalHeight(e.currentTarget.naturalHeight)
            }}
          />
        </picture>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div<{ ratio: { x: number; y: number } }>`
  position: relative;
  height: 0;
  padding-bottom: ${({ ratio }) => `${(ratio.y / ratio.x) * 100}%`};
`

const Img = styled.img<{ fit: 'cover' | 'contain'; isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: ${({ fit }) => fit};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  border-radius: ${getProjectName() === 'avito' && '12px'};
`

const Placeholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  justify-content: center;
`
